import React, { useState } from 'react'
import { shareTask, unshareTask, getTasks } from '../utils/supabase'

const ShareModal = ({ task, onShare, onClose }) => {
  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  const handleShare = async (e) => {
    e.preventDefault()
    
    if (!email.trim()) {
      setError('Please enter an email address')
      return
    }
    
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(email.trim())) {
      setError('Please enter a valid email address')
      return
    }
    
    // Prevent sharing with task creator (self-sharing)
    if (email.trim() === task.creator_email) {
      setError('Cannot share task with yourself - you already own it')
      return
    }
    
    // Check if already shared with this email
    const isAlreadyShared = task.task_shares?.some(share => 
      share.shared_with_email === email.trim()
    )
    
    if (isAlreadyShared) {
      setError('Task is already shared with this email')
      return
    }
    
    setLoading(true)
    setError('')
    setSuccess('')
    
    try {
      const { error } = await shareTask(task.id, email.trim())
      if (error) throw error
      
      setSuccess(`Task shared successfully with ${email.trim()}`)
      setEmail('')
      
      // Auto-close after 2 seconds
      setTimeout(async () => {
        // Get updated task with new sharing info
        const { data } = await getTasks()
        const updatedTask = data.find(t => t.id === task.id)
        onShare(updatedTask || task)
      }, 2000)
      
    } catch (error) {
      console.error('Error sharing task:', error)
      setError('Failed to share task: ' + error.message)
    } finally {
      setLoading(false)
    }
  }

  const handleUnshare = async (sharedEmail) => {
    setLoading(true)
    setError('')
    
    try {
      const { error } = await unshareTask(task.id, sharedEmail)
      if (error) throw error
      
      setSuccess(`Unshared task with ${sharedEmail}`)
      
      // Auto-refresh after 1 second
      setTimeout(async () => {
        // Get updated task with removed sharing info
        const { data } = await getTasks()
        const updatedTask = data.find(t => t.id === task.id)
        onShare(updatedTask || task)
      }, 1000)
      
    } catch (error) {
      console.error('Error unsharing task:', error)
      setError('Failed to unshare task: ' + error.message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="modal-overlay">
      <div className="modal">
        <h2>Share Task: {task.title}</h2>
        
        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}
        
        <div className="share-section">
          <h3>Currently Shared With:</h3>
          {task.task_shares && task.task_shares.length > 0 ? (
            <div className="shared-list">
              {task.task_shares.map((share, index) => (
                <div key={index} className="shared-item">
                  <span className="shared-email">{share.shared_with_email}</span>
                  <button
                    onClick={() => handleUnshare(share.shared_with_email)}
                    disabled={loading}
                    className="btn-small btn-danger"
                    title="Remove sharing"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p className="no-shares">This task is not shared with anyone</p>
          )}
        </div>
        
        <div className="share-section">
          <h3>Share with New User:</h3>
          <form onSubmit={handleShare}>
            <div className="form-group">
              <label htmlFor="shareEmail">Email address:</label>
              <input
                id="shareEmail"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="user@example.com"
                disabled={loading}
                required
              />
            </div>
            
            <div className="form-actions">
              <button
                type="submit"
                disabled={loading}
                className="btn btn-primary"
              >
                {loading ? 'Sharing...' : 'Share Task'}
              </button>
            </div>
          </form>
        </div>
        
        <div className="modal-actions">
          <button
            onClick={onClose}
            disabled={loading}
            className="btn"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )
}

export default ShareModal